import {
  KeyValueCustomSelect,
  Text,
  useSelectState,
} from "@smartsuite/react-ui/lib";
import { CheckmarkOption } from "@smartsuite/react-ui/lib/components/selects/components/CheckmarkOption/CheckmarkOption";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { hasTextAttrs } from "../../canvas/state/attributes";
import { DocumentDesignItem } from "../../canvas/state/types";
import { standardOSFontsSet } from "../SidebarSelectionConfig/constants";
import { showElementWithoutImplementation } from "../utils";

export interface FontFamilySelectorFieldProps {
  selected: DocumentDesignItem;
  onChangeSelected: (modifiedItem: DocumentDesignItem) => void;
}

const FontFamilySelectorFieldRaw: React.FC<FontFamilySelectorFieldProps> = ({
  selected,
  onChangeSelected,
}) => {
  const [fontFamilySelectState, setFontFamilySelectState] = useSelectState();
  const [fontFamilyOptions, setFontFamilyOptions] = useState<
    Array<{
      value: string;
      label: string;
    }>
  >([]);

  const fontFamilyOptionsSet = useMemo(() => {
    return new Set(fontFamilyOptions.map((f) => f.value));
  }, [fontFamilyOptions]);

  useEffect(() => {
    // On mount, grab all OS available fonts and populate the dropdown with the values.
    async function getAvailableOSFonts(): Promise<void> {
      await document.fonts.ready;
      const fontAvailable = new Set<string>();
      for (const font of standardOSFontsSet.values()) {
        if (document.fonts.check(`12px "${font}"`)) {
          fontAvailable.add(font);
        }
      }
      setFontFamilyOptions(
        [...fontAvailable.values()].map((f) => ({
          value: f,
          label: f,
        })),
      );
    }

    void getAvailableOSFonts();
  }, []);

  useEffect(() => {
    if (
      hasTextAttrs(selected) &&
      selected.fontFamily &&
      fontFamilyOptionsSet.size > 0 &&
      !fontFamilyOptionsSet.has(selected.fontFamily)
    ) {
      onChangeSelected({ ...selected, fontFamily: "Arial" });
    }
  }, [selected, onChangeSelected, fontFamilyOptionsSet]);

  const getItemFontFamily = useCallback(
    (fontFamily: string): string => {
      return fontFamilyOptionsSet.has(fontFamily) ? fontFamily : "Arial";
    },
    [fontFamilyOptionsSet],
  );

  // Renders the font name styled by the font family itself. If the font is symbol-based,
  // then also append its name un-styled to it in the list.
  const renderStylizedText = (
    label: string,
    value: string,
  ): React.ReactNode => {
    let fontNameUnStylized: React.ReactNode = null;
    if (["Webdings", "Wingdings"].includes(label)) {
      fontNameUnStylized = (
        <Text color="tertiary">
          &nbsp;<i>({label})</i>
        </Text>
      );
    }
    return (
      <>
        <Text styles={{ fontFamily: value }}>{label}</Text>
        {fontNameUnStylized}
      </>
    );
  };

  return (
    <>
      {hasTextAttrs(selected) && (
        <div>
          <Text
            weight="medium"
            color={fontFamilySelectState ? "link" : "primary"}
            wrapMode="ellipsis"
            styles={{ marginBottom: 4 }}
          >
            Font
          </Text>
          {showElementWithoutImplementation && (
            <div className="sidebar-selection-config__button-config">
              <button>Preview fonts (?)</button>
              <button>Set as default font (TODO)</button>
            </div>
          )}
          <KeyValueCustomSelect<"value", "label", (typeof fontFamilyOptions)[0]>
            state={fontFamilySelectState}
            className="font-family-selector-field__select"
            options={fontFamilyOptions}
            showClear={false}
            placeholderArrowStyle="filled-dark"
            value={getItemFontFamily(selected.fontFamily)}
            showFilterGreaterThan={4}
            searchPlaceholder="Search for a font"
            renderOption={({ label, value }, active) => (
              <CheckmarkOption rightAlignSelected={true} selected={active}>
                {renderStylizedText(label, value)}
              </CheckmarkOption>
            )}
            renderSelectedOption={({ label, value }) =>
              renderStylizedText(label, value)
            }
            onChange={(value) =>
              onChangeSelected({ ...selected, fontFamily: value })
            }
            onOpenedChange={setFontFamilySelectState}
          />
        </div>
      )}
    </>
  );
};

export const FontFamilySelectorField = memo(FontFamilySelectorFieldRaw);
